import {useSlots} from '@primer/react/experimental'
import {Stack} from '@primer/react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react'
import {clsx} from 'clsx'
import styles from './styles.module.css'
import {ssrSafeLocation} from '@github-ui/ssr-utils'

/**
 * A partial definition of the qbsearch-input web component's element interface
 */
export type SearchInputElement = Element & {
  handleExpand: () => void
  closeDialog: () => void
  syncRichButtonText: () => void
}

/**
 * A block of text in the search input, representing either a filter, constant text, entity, or value
 */
export type SearchTextBlock = {
  type: 'filter' | 'constant' | 'entity' | 'value'
  value: string
}

const TopNavContext = createContext<{
  hasLocalNavigation: boolean
  searchOpen: boolean
  searchTextBlocks: SearchTextBlock[]
  openSearch: () => void
  centerRef: React.RefObject<HTMLDivElement | null>
  isSearchPage: boolean
}>({
  hasLocalNavigation: false,
  searchOpen: false,
  searchTextBlocks: [],
  openSearch: () => {},
  centerRef: {current: null},
  isSearchPage: false,
})

export type TopNavProps = React.PropsWithChildren<{
  /** Whether the navigation has local navigation tabs below the main bar */
  hasLocalNavigation: boolean
  /** Whether the search input is on the search page */
  isSearchPage: boolean
}>

export function TopNavProvider({children, hasLocalNavigation, isSearchPage}: TopNavProps) {
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchTextBlocks, setSearchTextBlocks] = useState<SearchTextBlock[]>([])
  const searchInputRef = useRef<SearchInputElement | null>(null)
  const centerRef = useRef<HTMLDivElement | null>(null)

  const initializeSearch = useCallback(() => {
    // If qbsearch-input is already initialized, do nothing
    if (searchInputRef.current && 'syncRichButtonText' in searchInputRef.current) {
      return
    }

    // Move the search input into the center section of the navigation bar
    const searchInput = document.querySelector('qbsearch-input')
    if (!searchInput) return

    searchInputRef.current = searchInput as SearchInputElement
    searchInputRef.current.setAttribute('hidden', 'true')
    centerRef.current?.appendChild(searchInputRef.current)

    // Move the CSRF token input since it needs to be a sibling of the search input
    const suggestionsTokenInput = document.querySelector('.js-data-jump-to-suggestions-path-csrf')
    if (suggestionsTokenInput) {
      centerRef.current?.appendChild(suggestionsTokenInput)
    }
    searchInputRef.current.syncRichButtonText()
  }, [])

  const openSearch = useCallback(() => {
    initializeSearch()
    searchInputRef.current?.handleExpand()
  }, [initializeSearch])

  const onCloseSearch = useCallback(() => {
    setSearchOpen(false)
    searchInputRef.current?.closeDialog()
  }, [])

  const onOpenSearch = useCallback(() => {
    searchInputRef.current?.removeAttribute('hidden')
    setSearchOpen(true)
  }, [])

  const onUpdateText = useCallback((event: Event) => {
    if (!(event instanceof CustomEvent)) return
    setSearchTextBlocks(event.detail.searchTextBlocks)
  }, [])

  useEffect(() => {
    // Initialize search input on mount
    initializeSearch()

    document.addEventListener('qbsearch-input:close', onCloseSearch)
    document.addEventListener('qbsearch-input:expand', onOpenSearch)
    document.addEventListener('qbsearch-input:updateText', onUpdateText)

    return () => {
      document.removeEventListener('qbsearch-input:close', onCloseSearch)
      document.removeEventListener('qbsearch-input:expand', onOpenSearch)
      document.removeEventListener('qbsearch-input:updateText', onUpdateText)
    }
  }, [onCloseSearch, onOpenSearch, onUpdateText, initializeSearch])

  const context = useMemo(
    () => ({hasLocalNavigation, searchOpen, searchTextBlocks, centerRef, openSearch, isSearchPage}),
    [hasLocalNavigation, searchOpen, searchTextBlocks, openSearch, isSearchPage],
  )

  return <TopNavContext.Provider value={context}>{children}</TopNavContext.Provider>
}

/**
 * React hook to access the TopNav context.
 *
 * Returns an object with the following values:
 * - `hasLocalNavigation` (boolean): Whether local navigation tabs are present below the main bar.
 * - `searchOpen` (boolean): Whether the search input is currently open.
 * - `searchTextBlocks` (SearchTextBlock[]): The current blocks of text in the search input.
 * - `openSearch` (function): Function to open/expand the search input.
 * - `centerRef` (React.RefObject<HTMLDivElement | null>): Ref to the center section of the navigation bar.
 * - `isSearchPage` (boolean): Whether the search input is on the search page.
 *
 * Should be used within components that are descendants of `TopNavProvider` or `TopNav`.
 */
export function useTopNavContext() {
  return useContext(TopNavContext)
}

function TopNavComponent({children}: PropsWithChildren<{}>) {
  const {searchTextBlocks, isSearchPage} = useTopNavContext()
  const [{left, center, right, above}, rest] = useSlots(children, {
    left: Left,
    center: Center,
    right: Right,
    above: Above,
  })

  const hideTopNav = useMemo(() => {
    const urlParams = new URLSearchParams(ssrSafeLocation.search)

    // hide top nav on the search page when query param and search text blocks are empty
    return isSearchPage && !urlParams.get('q')?.trim() && !searchTextBlocks.length
  }, [isSearchPage, searchTextBlocks])

  return (
    <Stack
      as="header"
      className={clsx(styles.appHeader, hideTopNav && styles.hideTopNav)}
      role="banner"
      aria-label="Global Navigation Menu"
      gap="none"
    >
      {above}
      <Stack direction="horizontal" align="center" justify="center">
        {left}
        {center}
        {right}
      </Stack>
      {rest}
    </Stack>
  )
}

function Left(props: React.PropsWithChildren<{}>) {
  const {hasLocalNavigation} = useContext(TopNavContext)
  return (
    <Stack
      data-testid="top-nav-left"
      direction="horizontal"
      className={clsx(styles.left, hasLocalNavigation && styles.withLocalNavigation)}
      gap="condensed"
      padding="normal"
    >
      {props.children}
    </Stack>
  )
}

function Center(props: React.PropsWithChildren<{className?: string}>) {
  const {hasLocalNavigation, centerRef} = useContext(TopNavContext)
  return (
    <Stack
      data-testid="top-nav-center"
      direction="horizontal"
      gap="condensed"
      padding="normal"
      ref={centerRef}
      className={clsx(styles.center, hasLocalNavigation && styles.withLocalNavigation, props.className)}
    >
      {props.children}
    </Stack>
  )
}

function Right(props: React.PropsWithChildren<{}>) {
  const {hasLocalNavigation} = useContext(TopNavContext)
  return (
    <Stack
      data-testid="top-nav-right"
      direction="horizontal"
      align="center"
      gap="condensed"
      padding="normal"
      className={clsx(styles.right, hasLocalNavigation && styles.withLocalNavigation)}
    >
      {props.children}
    </Stack>
  )
}

function Above(props: React.PropsWithChildren<{}>) {
  return <>{props.children}</>
}

/**
 * The TopNav component lays the structure for the top navigation bar within product.
 * Use the `TopNav.Above`, `TopNav.Left`, `TopNav.Center`, and `TopNav.Right` slots to define the content above, left, center, and right sides of the navigation bar.
 * - `TopNav.Above`: Content rendered above the main navigation bar (e.g., banners, alerts).
 * - `TopNav.Left`, `TopNav.Center`, `TopNav.Right`: Content rendered on the left, center, and right of the navigation bar, respectively.
 * Any additional children will be shown below the initial bar (such as Local Navigation).
 */
export const TopNav = Object.assign(TopNavComponent, {Above, Left, Center, Right})

try{ TopNavProvider.displayName ||= 'TopNavProvider' } catch {}
try{ TopNavComponent.displayName ||= 'TopNavComponent' } catch {}
try{ Left.displayName ||= 'Left' } catch {}
try{ Center.displayName ||= 'Center' } catch {}
try{ Right.displayName ||= 'Right' } catch {}
try{ Above.displayName ||= 'Above' } catch {}
try{ TopNav.displayName ||= 'TopNav' } catch {}